<template>
    <DefaultHeader />

    <main v-if="!$store.state.hiddenBody" class="alarm-types-page">
        <div class="alarm-types-container">

            <header class="page-heading">
                <div>
                    <div class="eyebrow">CONFIGURATION</div>
                    <h1>Types d'alarmes</h1>
                    <p>Consultez le référentiel des types d'alarmes enregistrés dans le système.</p>
                </div>
            </header>

            <section class="list-panel" aria-labelledby="alarm-types-list-title">

                <div class="panel-toolbar">
                    <div>
                        <h2 id="alarm-types-list-title">Répertoire</h2>
                        <p>
                            {{ filteredAlarmTypes.length }}
                            type{{ filteredAlarmTypes.length > 1 ? 's' : '' }}
                            d'alarme affiché{{ filteredAlarmTypes.length > 1 ? 's' : '' }}
                            <template v-if="search">
                                sur {{ alarmTypes.length }}
                            </template>
                        </p>
                    </div>

                    <div class="filters">
                        <label class="search-wrap">
                            <span class="sr-only">Rechercher un type d'alarme</span>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="11" cy="11" r="7" />
                                <path d="m16.5 16.5 4 4" />
                            </svg>
                            <input v-model.trim="search" type="search" placeholder="Rechercher une alarme…" />
                        </label>
                    </div>
                </div>

                <div v-if="loading" class="state" role="status">
                    <span class="loader" aria-hidden="true"></span>
                    Chargement des types d'alarmes…
                </div>

                <div v-else-if="loadError" class="state" role="alert">
                    <h3>Chargement impossible</h3>
                    <p>{{ loadError }}</p>
                    <button v-if="!accessDenied" class="text-button" type="button" @click="loadAlarmTypes">
                        Réessayer
                    </button>
                </div>

                <div v-else-if="!alarmTypes.length" class="state">
                    <h3>Aucun type d'alarme</h3>
                    <p>Aucun type d'alarme n'est enregistré dans la base de données.</p>
                </div>

                <div v-else-if="!filteredAlarmTypes.length" class="state">
                    <h3>Aucun résultat</h3>
                    <p>Aucun type d'alarme ne correspond à votre recherche.</p>
                    <button class="text-button" type="button" @click="search = ''">
                        Effacer la recherche
                    </button>
                </div>

                <div v-else class="table-scroll">
                    <table class="alarm-types-table">
                        <thead>
                            <tr>
                                <th scope="col" :aria-sort="ariaSort('id')">
                                    <button type="button" class="sort-button" @click="changeSort('id')">
                                        ID
                                        <span class="sort-indicator" :class="{ active: sortKey === 'id' }"
                                            aria-hidden="true">{{
                                            sortSymbol('id') }}</span>
                                    </button>
                                </th>

                                <th scope="col" :aria-sort="ariaSort('name')">
                                    <button type="button" class="sort-button" @click="changeSort('name')">
                                        Nom
                                        <span class="sort-indicator" :class="{ active: sortKey === 'name' }"
                                            aria-hidden="true">{{
                                            sortSymbol('name') }}</span>
                                    </button>
                                </th>

                                <th scope="col" :aria-sort="ariaSort('severity')">
                                    <button type="button" class="sort-button" @click="changeSort('severity')">
                                        Sévérité
                                        <span class="sort-indicator" :class="{ active: sortKey === 'severity' }"
                                            aria-hidden="true">{{
                                            sortSymbol('severity') }}</span>
                                    </button>
                                </th>

                                <th scope="col" :aria-sort="ariaSort('display')">
                                    <button type="button" class="sort-button" @click="changeSort('display')">
                                        Affichage
                                        <span class="sort-indicator" :class="{ active: sortKey === 'display' }"
                                            aria-hidden="true">{{
                                            sortSymbol('display') }}</span>
                                    </button>
                                </th>

                                <th scope="col" :aria-sort="ariaSort('allowedSMS')">
                                    <button type="button" class="sort-button" @click="changeSort('allowedSMS')">
                                        SMS autorisés
                                        <span class="sort-indicator" :class="{ active: sortKey === 'allowedSMS' }"
                                            aria-hidden="true">{{
                                            sortSymbol('allowedSMS') }}</span>
                                    </button>
                                </th>

                                <th scope="col" :aria-sort="ariaSort('contactId')">
                                    <button type="button" class="sort-button" @click="changeSort('contactId')">
                                        Contact ID
                                        <span class="sort-indicator" :class="{ active: sortKey === 'contactId' }"
                                            aria-hidden="true">{{
                                            sortSymbol('contactId') }}</span>
                                    </button>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="alarm in filteredAlarmTypes" :key="alarm.id">

                                <td class="id-cell">
                                    #{{ alarm.id }}
                                </td>

                                <td class="name-cell">
                                    {{ alarm.name || 'Sans nom' }}
                                </td>

                                <td>
                                    <span class="severity" :class="severityClass(alarm.severity)">
                                        <span class="dot"></span>
                                        {{ severityLabel(alarm.severity) }}
                                    </span>
                                </td>

                                <td>
                                    <span class="flag" :class="flagClass(alarm.display)">
                                        <span class="dot"></span>
                                        {{ flagLabel(alarm.display) }}
                                    </span>
                                </td>

                                <td>
                                    <span class="flag" :class="flagClass(alarm.allowedSMS)">
                                        <span class="dot"></span>
                                        {{ flagLabel(alarm.allowedSMS) }}
                                    </span>
                                </td>

                                <td class="contact-cell">
                                    {{ alarm.contactId ?? '—' }}
                                </td>

                            </tr>
                        </tbody>
                    </table>
                </div>

            </section>
        </div>
    </main>

    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue';
import DefaultFooter from '@/components/DefaultFooter.vue';
import AlarmTypeRepository from '@/repositories/alarmType.repo.js';

const severityLabels = {
    INFO: 'Information',
    WARNING: 'Avertissement',
    CRITICAL: 'Critique'
};

const collator = new Intl.Collator('fr', {
    numeric: true,
    sensitivity: 'base'
});

export default {
    name: 'AlarmTypesView',

    components: {
        DefaultHeader,
        DefaultFooter
    },

    data() {
        return {
            alarmTypes: [],
            loading: false,
            loadError: '',
            accessDenied: false,

            search: '',
            sortKey: 'id',
            sortDirection: 'asc'
        };
    },

    computed: {
        filteredAlarmTypes() {
            const needle = this.search.toLocaleLowerCase('fr').trim();

            const filtered = this.alarmTypes.filter(alarm => {
                if (!needle) return true;

                const searchable = [
                    alarm.id,
                    alarm.name,
                    alarm.severity,
                    this.severityLabel(alarm.severity),
                    this.flagLabel(alarm.display),
                    this.flagLabel(alarm.allowedSMS),
                    alarm.contactId
                ]
                    .filter(value => value !== null && value !== undefined)
                    .map(value => String(value).toLocaleLowerCase('fr'))
                    .join(' ');

                return searchable.includes(needle);
            });

            const direction = this.sortDirection === 'asc' ? 1 : -1;

            return [...filtered].sort((a, b) => {
                const valueA = this.sortValue(a, this.sortKey);
                const valueB = this.sortValue(b, this.sortKey);

                // Les valeurs absentes restent en fin de liste.
                const emptyA = valueA === null || valueA === undefined || valueA === '';
                const emptyB = valueB === null || valueB === undefined || valueB === '';

                if (emptyA && emptyB) return 0;
                if (emptyA) return 1;
                if (emptyB) return -1;

                let comparison;

                if (['id', 'display', 'allowedSMS'].includes(this.sortKey)) {
                    comparison = Number(valueA) - Number(valueB);
                } else {
                    comparison = collator.compare(String(valueA), String(valueB));
                }

                return comparison * direction;
            });
        }
    },

    mounted() {
        this.loadAlarmTypes();
    },

    methods: {
        async loadAlarmTypes() {
            this.loading = true;
            this.loadError = '';
            this.accessDenied = false;

            try {
                const result = await AlarmTypeRepository.getAlarmTypes();

                if (!Array.isArray(result)) {
                    throw new Error('Format de réponse invalide.');
                }

                this.alarmTypes = result;

            } catch (err) {
                this.alarmTypes = [];

                const status = err?.response?.status;

                if (status === 401 || status === 403) {
                    this.accessDenied = true;
                    this.loadError = "Vous ne disposez pas des droits requis pour consulter les types d'alarmes.";
                } else {
                    this.loadError = "Impossible de charger les types d'alarmes.";
                }

                console.error(err);

            } finally {
                this.loading = false;
            }
        },

        changeSort(key) {
            if (this.sortKey === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortKey = key;
                this.sortDirection = 'asc';
            }
        },

        sortSymbol(key) {
            if (this.sortKey !== key) return '↕';
            return this.sortDirection === 'asc' ? '↑' : '↓';
        },

        ariaSort(key) {
            if (this.sortKey !== key) return 'none';
            return this.sortDirection === 'asc' ? 'ascending' : 'descending';
        },

        sortValue(alarm, key) {
            if (key === 'severity') {
                return this.severityLabel(alarm.severity);
            }

            if (key === 'display' || key === 'allowedSMS') {
                return alarm[key] == null ? null : Number(Boolean(alarm[key]));
            }

            return alarm[key];
        },

        severityLabel(value) {
            return severityLabels[value] || value || '—';
        },

        severityClass(value) {
            switch (value) {
                case 'INFO': return 'info';
                case 'WARNING': return 'warning';
                case 'CRITICAL': return 'critical';
                default: return 'neutral';
            }
        },

        flagLabel(value) {
            if (value === null || value === undefined) return '—';
            return value ? 'Oui' : 'Non';
        },

        flagClass(value) {
            if (value === null || value === undefined) return 'neutral';
            return value ? 'yes' : 'no';
        }
    }
};
</script>

<style scoped>
.alarm-types-page {
    min-height: 65vh;
    background: #f5f7fb;
    color: #17243b;
    padding: 42px 24px 70px;
}

.alarm-types-container {
    max-width: 1420px;
    margin: 0 auto;
}

/* En-tête */

.page-heading {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    align-items: center;
    margin-bottom: 30px;
}

.eyebrow {
    color: #627c9e;
    font-size: 11px;
    letter-spacing: .16em;
    font-weight: 800;
    margin-bottom: 9px;
}

h1 {
    font-size: clamp(28px, 3vw, 36px);
    font-weight: 750;
    letter-spacing: -.035em;
    margin: 0 0 7px;
}

.page-heading p,
.panel-toolbar p {
    margin: 0;
    color: #6c7890;
    font-size: 14px;
}

/* Panneau */

.list-panel {
    min-width: 0;
    background: white;
    border-radius: 16px;
    border: 1px solid #e3e9f1;
    box-shadow: 0 10px 28px rgba(20, 43, 74, .045);
    overflow: hidden;
}

.panel-toolbar {
    padding: 24px 26px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    border-bottom: 1px solid #e9edf3;
}

.panel-toolbar h2 {
    font-size: 18px;
    font-weight: 750;
    margin: 0 0 4px;
}

/* Recherche */

.filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.search-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid #dce4ee;
    border-radius: 9px;
    padding: 0 12px;
    background: #fff;
    min-width: 280px;
    margin: 0;
}

.search-wrap svg {
    width: 17px;
    height: 17px;
    stroke: #8999ae;
    stroke-width: 2;
    fill: none;
    flex: none;
}

.search-wrap input {
    border: 0;
    outline: 0;
    padding: 11px 0;
    background: transparent;
    width: 100%;
    min-width: 0;
    font-size: 13px;
}

.search-wrap:focus-within {
    outline: 2px solid #93b5e3;
    outline-offset: 1px;
}

/* Tableau */

.table-scroll {
    width: 100%;
    overflow-x: auto;
}

.alarm-types-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
}

th {
    background: #fbfcfe;
    color: #7c8799;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .08em;
    padding: 0;
    white-space: nowrap;
    font-weight: 800;
}

.sort-button {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-weight: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    text-align: left;
    cursor: pointer;
    padding: 15px 25px;
    white-space: nowrap;
}

.sort-button:hover {
    color: #1b4f95;
    background: #f2f6fc;
}

.sort-button:focus-visible {
    outline: 2px solid #93b5e3;
    outline-offset: -3px;
}

.sort-indicator {
    color: #aeb9c8;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
}

.sort-indicator.active {
    color: #1b4f95;
}

td {
    border-top: 1px solid #edf0f5;
    padding: 17px 25px;
    font-size: 13px;
    color: #344257;
}

tbody tr:hover td {
    background: #f9fbfe;
}

.id-cell {
    color: #7a8ba3;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.name-cell {
    color: #202e44;
    font-weight: 750;
    min-width: 190px;
}

.contact-cell {
    color: #65728a;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

/* Sévérité et indicateurs */

.severity,
.flag {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border-radius: 99px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 750;
    white-space: nowrap;
}

.dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    flex: none;
}

.severity.info {
    color: #2b638e;
    background: #eaf3fc;
}

.severity.warning {
    color: #a56a0a;
    background: #fff5df;
}

.severity.critical {
    color: #b43636;
    background: #fff0f0;
}

.flag.yes {
    color: #17794b;
    background: #eaf7f0;
}

.flag.no,
.flag.neutral,
.severity.neutral {
    color: #677488;
    background: #eef1f5;
}

/* États */

.state {
    min-height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    padding: 35px;
    color: #687892;
}

.state h3 {
    color: #21324b;
    font-size: 18px;
    margin: 0;
}

.state p {
    max-width: 380px;
    margin: 0;
}

.loader {
    width: 27px;
    height: 27px;
    border: 3px solid #dae7f4;
    border-top-color: #1b4f95;
    border-radius: 50%;
    animation: spin .8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.text-button {
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    color: #1b4f95;
    font-weight: 700;
}

.text-button:hover {
    text-decoration: underline;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/* Responsive */

@media (max-width: 620px) {
    .alarm-types-page {
        padding: 26px 14px 50px;
    }

    .page-heading {
        align-items: stretch;
        flex-direction: column;
    }

    .panel-toolbar {
        padding: 20px 16px;
    }

    .filters,
    .search-wrap {
        width: 100%;
        min-width: 0;
    }

    .sort-button {
        padding: 14px 13px;
    }

    td {
        padding: 14px 13px;
    }
}
</style>